import { useCallback, useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { SERVICE_PREFIX } from "@common/constants";
import {
    agentChatStreamEventSchema,
    type AgentChatStreamEvent,
} from "@common/schemas/domains/agentChat/agent-chat-events.schema";
import type {
    AgentChatIntent,
    ToolTraceEntry,
} from "@common/schemas/domains/agentChat/agent-chat.schema";
import { getAccessToken } from "@lib/auth0";
import { appendTurnToCache, type SendMessageInput } from "./agentChat.api";

// ── Live response state ─────────────────────────────────────────────────────

export interface LiveToolRow {
    traceIndex: number;
    name: string;
    input: Record<string, unknown>;
    status: "running" | "done" | "error";
    // Present once the tool finishes — drives the persisted-card rendering.
    entry?: ToolTraceEntry;
}

export interface LiveIntent {
    resolvedIntent: AgentChatIntent;
    reasoning: string;
    toolCandidates: Array<{ name: string; probability: number }>;
    skillCandidates?: Array<{ name: string }>;
}

export interface AgentChatLiveResponseState {
    intent: LiveIntent | null;
    // Keyed by `traceIndex` so the reducer is order-independent and immune to
    // orphaned / coalesced frames.
    rows: Map<number, LiveToolRow>;
}

export function createEmptyLiveResponseState(): AgentChatLiveResponseState {
    return { intent: null, rows: new Map() };
}

// Normalizes a persisted/completed trace entry into a tool row. Shared by the
// reducer (tool_completed) and the persisted-message renderer so live and saved
// rows look identical.
export function liveRowFromEntry(
    entry: ToolTraceEntry,
    traceIndex: number,
): LiveToolRow {
    const failed =
        entry.kind === "tool" ? entry.isError : entry.status === "failed";
    return {
        traceIndex,
        name: entry.kind === "tool" ? entry.name : "web_search",
        input:
            entry.kind === "tool"
                ? entry.input
                : { searchCallId: entry.searchCallId },
        status: failed ? "error" : "done",
        entry,
    };
}

// Upsert-by-traceIndex reducer. Only intent/tool events reach it — `error` and
// `message_complete` are terminal and handled by the hook.
export function applyStreamEventToLiveState(
    state: AgentChatLiveResponseState,
    event: AgentChatStreamEvent,
): AgentChatLiveResponseState {
    if (event.event === "intent_detected") {
        return { ...state, intent: event.payload };
    }
    if (event.event === "tool_started") {
        const rows = new Map(state.rows);
        const existing = rows.get(event.payload.traceIndex);
        rows.set(
            event.payload.traceIndex,
            existing
                ? {
                      ...existing,
                      name: event.payload.name,
                      input: event.payload.input,
                  }
                : {
                      traceIndex: event.payload.traceIndex,
                      name: event.payload.name,
                      input: event.payload.input,
                      status: "running",
                  },
        );
        return { ...state, rows };
    }
    if (event.event === "tool_completed") {
        const rows = new Map(state.rows);
        const { traceIndex, entry } = event.payload;
        const completed = liveRowFromEntry(entry, traceIndex);
        const existing = rows.get(traceIndex);
        rows.set(
            traceIndex,
            existing
                ? { ...existing, status: completed.status, entry }
                : completed,
        );
        return { ...state, rows };
    }
    return state;
}

// ── SSE-over-POST reader ────────────────────────────────────────────────────

const errorBodySchema = z.object({
    message: z.string().optional(),
    error: z.string().optional(),
});

async function extractErrorMessage(response: Response): Promise<string> {
    const fallback =
        response.status === 422
            ? "We couldn't process this — try again."
            : "Send failed";
    try {
        const parsed = errorBodySchema.safeParse(
            JSON.parse(await response.text()),
        );
        if (parsed.success) {
            const message = parsed.data.message ?? parsed.data.error;
            if (typeof message === "string" && message.length > 0) {
                return message;
            }
        }
    } catch {
        // Non-JSON body — fall through to the generic message.
    }
    return fallback;
}

// Parses one `event:`/`data:` SSE frame. Returns null for heartbeats / comment
// frames so the reader can skip them.
function parseFrame(rawFrame: string): AgentChatStreamEvent | null {
    const trimmed = rawFrame.trim();
    if (trimmed.length === 0 || trimmed.startsWith(":")) {
        return null;
    }
    let eventName: string | undefined;
    const dataLines: string[] = [];
    for (const line of trimmed.split("\n")) {
        if (line.startsWith("event:")) {
            eventName = line.slice("event:".length).trim();
        } else if (line.startsWith("data:")) {
            dataLines.push(line.slice("data:".length).trim());
        }
    }
    if (eventName === undefined) {
        return null;
    }
    return agentChatStreamEventSchema.parse({
        event: eventName,
        payload: JSON.parse(dataLines.join("\n")),
    });
}

interface StreamArgs {
    body: SendMessageInput;
    onEvent: (event: AgentChatStreamEvent) => void;
    signal?: AbortSignal;
}

// POSTs a message and reads the SSE response with fetch + ReadableStream,
// forwarding every event except `error` to `onEvent`. Rejects on a pre-stream
// HTTP failure, a server `error` frame, or a stream that ends without
// `message_complete` — so callers get a single recovery path.
export async function streamAgentChatMessage({
    body,
    onEvent,
    signal,
}: StreamArgs): Promise<void> {
    const token = await getAccessToken();
    const response = await fetch(
        `/${SERVICE_PREFIX.MAIN_APP}/agent-chat/messages/stream`,
        {
            method: "POST",
            headers: {
                authorization: token ? `Bearer ${token}` : "",
                "content-type": "application/json",
            },
            body: JSON.stringify(body),
            signal,
        },
    );

    if (!response.ok) {
        throw new Error(await extractErrorMessage(response));
    }
    if (!response.body) {
        throw new Error("Streaming is not supported in this environment.");
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    let sawComplete = false;

    // Forwards one frame; returns true if it was the terminal
    // `message_complete`. Throws on a server `error` frame so the rejection
    // propagates to the caller's recovery path.
    const consumeFrame = (rawFrame: string): boolean => {
        const event = parseFrame(rawFrame);
        if (!event) {
            return false;
        }
        if (event.event === "error") {
            throw new Error(event.payload.message);
        }
        onEvent(event);
        return event.event === "message_complete";
    };

    try {
        for (;;) {
            const { done, value } = await reader.read();
            if (done) {
                break;
            }
            buffer += decoder.decode(value, { stream: true });
            let separatorIndex = buffer.indexOf("\n\n");
            while (separatorIndex !== -1) {
                const rawFrame = buffer.slice(0, separatorIndex);
                buffer = buffer.slice(separatorIndex + 2);
                if (consumeFrame(rawFrame)) {
                    sawComplete = true;
                }
                separatorIndex = buffer.indexOf("\n\n");
            }
        }
        if (!sawComplete) {
            throw new Error(
                "The response stream ended unexpectedly. Please try again.",
            );
        }
    } finally {
        reader.releaseLock();
    }
}

// ── Hook ────────────────────────────────────────────────────────────────────

// Fast tools finish in tens of ms, so `tool_started` and `tool_completed` reach
// the client almost together and React batches them into one render — the
// running spinner never paints. Hold each row in its `running` state for at
// least this long after `tool_started` before applying the completion, so the
// animated spinner is always perceptible (half a rotation of the 1s spinner).
const MIN_TOOL_RUNNING_MS = 500;

export function useAgentChatMessageStream() {
    const queryClient = useQueryClient();
    const [liveResponse, setLiveResponse] =
        useState<AgentChatLiveResponseState | null>(null);
    const [isStreaming, setIsStreaming] = useState(false);
    // traceIndex → `tool_started` timestamp, used to pace `tool_completed`.
    const toolStartedAtRef = useRef<Map<number, number>>(new Map());
    // Pending `tool_completed` timers, cleared on reset / unmount.
    const pendingTimersRef = useRef<Set<ReturnType<typeof setTimeout>>>(
        new Set(),
    );

    const clearPendingTimers = useCallback(() => {
        for (const timer of pendingTimersRef.current) {
            clearTimeout(timer);
        }
        pendingTimersRef.current.clear();
        toolStartedAtRef.current.clear();
    }, []);

    useEffect(() => clearPendingTimers, [clearPendingTimers]);

    const applyEvent = useCallback((event: AgentChatStreamEvent) => {
        setLiveResponse((previous) =>
            applyStreamEventToLiveState(
                previous ?? createEmptyLiveResponseState(),
                event,
            ),
        );
    }, []);

    const send = useCallback(
        async (
            input: SendMessageInput,
        ): Promise<{ conversationId: string }> => {
            setIsStreaming(true);
            clearPendingTimers();
            setLiveResponse(createEmptyLiveResponseState());
            let conversationId: string | null = null;
            try {
                await streamAgentChatMessage({
                    body: input,
                    onEvent: (event) => {
                        if (event.event === "message_complete") {
                            // Persisted cards own the rows now — drop any
                            // still-pending completion timer.
                            clearPendingTimers();
                            conversationId = event.payload.conversationId;
                            appendTurnToCache({
                                queryClient,
                                workspaceId: input.workspaceId,
                                conversationId: event.payload.conversationId,
                                userMessage: input.message,
                                assistantMessage:
                                    event.payload.assistantMessage,
                            });
                            setLiveResponse(null);
                            return;
                        }
                        if (event.event === "tool_started") {
                            toolStartedAtRef.current.set(
                                event.payload.traceIndex,
                                Date.now(),
                            );
                            applyEvent(event);
                            return;
                        }
                        if (event.event === "tool_completed") {
                            const startedAt =
                                toolStartedAtRef.current.get(
                                    event.payload.traceIndex,
                                ) ?? Date.now();
                            const remaining =
                                MIN_TOOL_RUNNING_MS - (Date.now() - startedAt);
                            if (remaining <= 0) {
                                applyEvent(event);
                                return;
                            }
                            const timer = setTimeout(() => {
                                pendingTimersRef.current.delete(timer);
                                applyEvent(event);
                            }, remaining);
                            pendingTimersRef.current.add(timer);
                            return;
                        }
                        applyEvent(event);
                    },
                });
                if (conversationId === null) {
                    throw new Error(
                        "The response stream ended unexpectedly. Please try again.",
                    );
                }
                return { conversationId };
            } catch (error) {
                clearPendingTimers();
                setLiveResponse(null);
                throw error;
            } finally {
                setIsStreaming(false);
            }
        },
        [queryClient, applyEvent, clearPendingTimers],
    );

    return { liveResponse, isStreaming, send };
}
